import { useState, useEffect } from 'react'
import { ChevronDown, ChevronUp, Play, Settings2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'

interface ReferenceCandidate {
  id: string
  name: string
}

interface SessionBrief {
  id: string
  brief_functionality: string | null
  brief_design_direction: string | null
  brief_reference_demo_ids: string[]
  duration_seconds: number
}

interface Props {
  session: SessionBrief
  referenceCandidates: ReferenceCandidate[]
  onRerun: (message: string) => void
  streaming: boolean
  hasMessages: boolean
}

export default function DemoBriefPanel({ session, referenceCandidates, onRerun, streaming, hasMessages }: Props) {
  // Start expanded if no messages yet (fresh demo), collapsed if agent has already run
  const [expanded, setExpanded] = useState(!hasMessages)
  const [functionality, setFunctionality] = useState(session.brief_functionality || '')
  const [designDirection, setDesignDirection] = useState(session.brief_design_direction || '')
  const [selectedRefs, setSelectedRefs] = useState<string[]>(session.brief_reference_demo_ids || [])
  const [duration, setDuration] = useState(session.duration_seconds || 17)
  const [saving, setSaving] = useState(false)

  // Auto-collapse when agent starts working
  useEffect(() => {
    if (streaming) setExpanded(false)
  }, [streaming])

  const toggleRef = (id: string) => {
    setSelectedRefs(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    )
  }

  const handleSaveAndRun = async () => {
    // Save brief first
    setSaving(true)
    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
      await fetch(`/demos/${session.id}/update_brief`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          brief_functionality: functionality.trim() || null,
          brief_design_direction: designDirection.trim() || null,
          brief_reference_demo_ids: selectedRefs,
          duration_seconds: duration,
        })
      })
    } finally {
      setSaving(false)
    }

    // Then fire the agent
    const parts: string[] = []
    if (functionality.trim()) parts.push(`Build a demo that shows:\n${functionality.trim()}`)
    if (designDirection.trim()) parts.push(`Design direction:\n${designDirection.trim()}`)
    parts.push(`Target duration: ${duration} seconds.`)
    onRerun(parts.join('\n\n'))
  }

  const summaryParts = [
    designDirection ? designDirection.slice(0, 50) + (designDirection.length > 50 ? '...' : '') : null,
    `${duration}s`,
  ].filter(Boolean)

  // Collapsed view — compact bar
  if (!expanded) {
    return (
      <div className="shrink-0 border-b border-gray-200 bg-gray-50">
        <button
          onClick={() => setExpanded(true)}
          className="flex w-full items-center gap-3 px-5 py-2.5 text-left transition-colors hover:bg-gray-100"
        >
          <Settings2 className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span className="flex-1 truncate text-xs text-gray-500">
            {summaryParts.length > 0 ? summaryParts.join(' · ') : 'Configure demo brief...'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        </button>
      </div>
    )
  }

  // Expanded view
  return (
    <div className="shrink-0 border-b border-gray-200 bg-gray-50">
      {/* Header with collapse toggle */}
      <div className="flex items-center justify-between px-5 pt-3 pb-1">
        <span className="text-xs font-semibold text-gray-600">Demo Brief</span>
        <button
          onClick={() => setExpanded(false)}
          className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
        >
          Collapse
          <ChevronUp className="h-3 w-3" />
        </button>
      </div>

      <div className="space-y-3 px-5 pb-4">
        {/* What to show */}
        <div className="space-y-1">
          <Label htmlFor="bp-func" className="text-[11px] text-gray-500">What to show</Label>
          <textarea
            id="bp-func"
            value={functionality}
            onChange={e => setFunctionality(e.target.value)}
            rows={3}
            placeholder="Product features and screens to demonstrate..."
            className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-xs leading-relaxed text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
          />
        </div>

        {/* Design direction */}
        <div className="space-y-1">
          <Label htmlFor="bp-design" className="text-[11px] text-gray-500">Design direction</Label>
          <textarea
            id="bp-design"
            value={designDirection}
            onChange={e => setDesignDirection(e.target.value)}
            rows={2}
            placeholder="e.g. Swiss minimal, dark emerald, like macrofactor.com..."
            className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-xs leading-relaxed text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
          />
        </div>

        {/* Reference demos + Duration — compact row */}
        <div className="flex items-end gap-3">
          {referenceCandidates.length > 0 && (
            <div className="flex-1 space-y-1">
              <Label className="text-[10px] text-gray-400">Reference demos</Label>
              <div className="flex flex-wrap gap-1">
                {referenceCandidates.slice(0, 6).map(ref => (
                  <button
                    key={ref.id}
                    type="button"
                    onClick={() => toggleRef(ref.id)}
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${
                      selectedRefs.includes(ref.id)
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300 hover:text-gray-600'
                    }`}
                  >
                    {ref.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="w-16 space-y-1">
            <Label htmlFor="bp-dur" className="text-[10px] text-gray-400">Duration</Label>
            <Input
              id="bp-dur"
              type="number"
              min={3}
              max={120}
              value={duration}
              onChange={e => setDuration(parseInt(e.target.value) || 17)}
              className="h-7 text-xs"
            />
          </div>
        </div>

        {/* Primary CTA */}
        <Button
          onClick={handleSaveAndRun}
          disabled={saving || streaming || !functionality.trim()}
          className="w-full"
          size="sm"
        >
          <Play className="mr-1.5 h-3.5 w-3.5" />
          {saving ? 'Saving...' : hasMessages ? 'Re-generate Demo' : 'Generate Demo'}
        </Button>
      </div>
    </div>
  )
}
